import * as R from 'ramda';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import ActionCell, { ActionButtonLink } from 'App/components/styles/ActionCell';
import { Button, Col, Row } from 'react-bootstrap';
import {
    allDiagnosisOption,
    allMedicationsOption,
    DISPLAY_OPERATORS,
    OPERATORS,
    REFERRAL_TYPES,
    TRIGGER_TYPES,
} from '../constants';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getFilterOperator, groupFilters } from '../utils';
import { errorHandler, hasLength, localize } from 'App/utils';

import AddRule from '../AddRule';
import AssessmentService from 'App/services/AssessmentService';
import { getReferralTypes } from 'App/services/RMSService';
import DeleteModal from 'App/components/DeleteModal';
import { useMachine } from '@xstate/react';
import { Formik } from 'formik';
import Alert from 'react-bootstrap/Alert';
import { Helmet } from 'react-helmet';
import { FaMinus, FaPlus } from 'react-icons/fa';
import styled from '@emotion/styled';
import toast from 'Shared/toast';
import { createMachine } from 'xstate';

import { protocolManagerRoute } from 'App/common/routeLookup';
import env from 'App/components/config/config';
import ProtocolService from 'App/services/ProtocolService';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';

import PageTitle from 'App/components/styles/PageTitle/PageTitle';
import PageWrapper from 'App/components/styles/PageWrapper/PageWrapper';
import ManualPageTable from 'App/components/ManualPageTable/ManualPageTable';
import Table from 'App/components/Table';
import { createRulesData } from './editProtocolReducer';
import useFetchProtocolTriggers from './useFetchProtocolTriggers';
import { queryClient } from 'Shared/queryClient';
import ProtocolBaseForm from '../ProtocolBaseForm';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';

import useEditProtocolContext from 'App/features/ProtocolManager/EditProtocolPage/useEditProtocolContext';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import { TextInput } from 'Shared/form/TextField';
import { dateTimeSlashesFormat } from 'App/services/DateService';

//#region State Machine
const MACHINE_STATES = Object.freeze({
    ADDING_RULE: 'ADDING_RULE',
    COPYING: 'COPYING',
    EDITING: 'EDITING',
    LOADING: 'LOADING',
    SAVING: 'SAVING',
});

const MACHINE_EVENTS = Object.freeze({
    ADD_RULE: 'ADD_RULE',
    ADD_RULE_COMPLETE: 'ADD_RULE_COMPLETE',
    CANCEL: 'CANCEL',
    COPY: 'COPY',
    DELETE_PROTOCOL: 'DELETE_PROTOCOL',
    DELETE_RULE: 'DELETE_RULE',
    DELETE_RULE_COMPLETE: 'DELETE_RULE_COMPLETE',
    EDIT: 'EDIT',
    SAVE: 'SAVE',
    SAVE_COMPLETE: 'SAVE_COMPLETE',
});

const editProtocolMachine = createMachine({
    initial: MACHINE_STATES.LOADING,
    states: {
        [MACHINE_STATES.ADDING_RULE]: {
            on: {
                [MACHINE_EVENTS.ADD_RULE_COMPLETE]: MACHINE_STATES.EDITING,
                [MACHINE_EVENTS.CANCEL]: MACHINE_STATES.EDITING,
            },
        },

        [MACHINE_STATES.COPYING]: {
            type: 'final',
        },

        [MACHINE_STATES.EDITING]: {
            on: {
                [MACHINE_EVENTS.ADD_RULE]: MACHINE_STATES.ADDING_RULE,

                [MACHINE_EVENTS.ADD_RULE_COMPLETE]: MACHINE_STATES.EDITING,

                [MACHINE_EVENTS.COPY]: MACHINE_STATES.COPYING,

                [MACHINE_EVENTS.SAVE]: MACHINE_STATES.SAVING,
            },
        },

        [MACHINE_STATES.LOADING]: {
            on: {
                [MACHINE_EVENTS.EDIT]: MACHINE_STATES.EDITING,
            },
        },
        [MACHINE_STATES.SAVING]: {
            on: {
                [MACHINE_EVENTS.SAVE_COMPLETE]: MACHINE_STATES.EDITING,
            },
        },
    },
});

//#endregion

function conditionRenderer(filters) {
    return R.compose(
        R.addIndex(R.map)((filterValues, valuesIdx, valuesArray) => {
            return R.addIndex(R.map)((filter, groupIdx, groupArray) => {
                const hasDisabledOn =
                    R.has('disabledOn', filter) &&
                    filter.disabledOn !== null &&
                    filter.disabledOn !== '0000-00-00 00:00:00';

                let operatorContent;
                if (filter.operator === OPERATORS.IS_ANSWERED) {
                    operatorContent = DISPLAY_OPERATORS.is_answered;
                } else if (hasDisabledOn) {
                    operatorContent = (
                        <>
                            {`${getFilterOperator(filter)} `}
                            <strong>{`"${filter.answerText}" `}</strong> disabled on
                            <strong>{` "${localize(filter.disabledOn, dateTimeSlashesFormat)}"`}</strong>
                        </>
                    );
                } else {
                    operatorContent = (
                        <>
                            {`${getFilterOperator(filter)} `}
                            <strong>{`"${filter.answerText}"`}</strong>
                        </>
                    );
                }

                return (
                    <div
                        key={filter.id}
                        style={{
                            color:
                                R.has('disabledOn', filter) &&
                                filter.disabledOn !== null &&
                                filter.disabledOn !== '0000-00-00 00:00:00'
                                    ? 'red'
                                    : 'none',
                        }}
                    >
                        {'when '}
                        <strong>{`"${filter.hidden ? '(HIDDEN) ' : ''}${filter.questionText}" `}</strong>
                        {operatorContent}
                        {groupArray[groupIdx + 1] ? ' or' : valuesArray[valuesIdx + 1] && ' and'}
                    </div>
                );
            }, filterValues);
        }),
        R.values,
        groupFilters
    )(filters);
}

const ChangeIndicator = styled('span')`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 0.875rem;
    white-space: nowrap;
`;

function shouldShowProtocolDelete({ hasDeleteTherigyProtocol, isReadOnly }) {
    return hasDeleteTherigyProtocol || !isReadOnly;
}

function isProtocolDeleteDisabled({ machineValue, isSubmitting, isReadOnly, hasDeleteTherigyProtocol }) {
    const result = R.anyPass([
        R.equals(MACHINE_STATES.LOADING),
        R.equals(MACHINE_STATES.SAVING),
        R.always(isSubmitting),
    ])(machineValue);

    // isReadOnly is mostly used to determine if the _page_ is readonly (i.e., view rather than create.)
    return hasDeleteTherigyProtocol ? result : result || isReadOnly;
}

//#endregion

const propTypes = {};

const defaultProps = {};

function rulesAccordionReducer(state, action) {
    switch (action) {
        case 'CLOSE':
            return false;
        case 'RELEASE':
            return null;
        case 'RESET':
            return null;
        default:
            return state;
    }
}

const EditProtocolPage = () => {
    const { protocolId } = useParams();
    const hasDeleteTherigyProtocol = useIsPermitted(PERMISSION_TYPES.DELETE_THERIGY_PROTOCOL);

    const { state, actions } = useEditProtocolContext();

    const [machineState, send] = useMachine(editProtocolMachine);

    const navigate = useNavigate();

    //#region State
    const {
        addedTriggers,
        protocolToDelete,
        deletedTriggers,
        diagnosisCodeOptions,
        isReadOnly,
        medicationOptions,
        protocol,
        selectedDiagnosisCodes,
        selectedMedications,
        showDeleteProtocolModal,
    } = state;

    const [triggerPage, setTriggerPage] = useState(1);
    const [triggerPageSize, setTriggerPageSize] = useState(10);
    const [triggerSort, setTriggerSort] = useState({ key: 'event', order: 'asc' });
    const [triggerFilters, setTriggerFilters] = useState({
        event: '',
        activityTriggered: '',
        conditions: '',
        dueDate: '',
    });
    const [debouncedFilters, setDebouncedFilters] = useState(triggerFilters);
    const rulesAccordionInitRef = useRef(false);
    const [rulesAccordionControlled, dispatchRulesAccordion] = useReducer(rulesAccordionReducer, null);

    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedFilters(triggerFilters);
            setTriggerPage(1);
        }, 800);
        return () => clearTimeout(id);
    }, [triggerFilters]);

    const { data: triggerResponse, isLoading: triggersLoading } = useFetchProtocolTriggers(protocolId, {
        page: triggerPage - 1,
        limit: triggerPageSize,
        sortKey: triggerSort.key,
        sortOrder: triggerSort.order,
        filterEvent: debouncedFilters.event,
        filterActivityTriggered: debouncedFilters.activityTriggered,
        filterConditions: debouncedFilters.conditions,
        filterDueDate: debouncedFilters.dueDate,
    });

    const serverTriggers = useMemo(() => {
        const rules = createRulesData(R.propOr([], 'data', triggerResponse));
        const deletedPasIds = R.map(R.prop('policyAssessmentScheduleId'), deletedTriggers);
        return R.reject((r) => R.includes(r.policyAssessmentScheduleId, deletedPasIds), rules);
    }, [triggerResponse, deletedTriggers]);

    const totalTriggers = R.propOr(0, 'total', triggerResponse);
    //#endregion

    //#region Actions
    const {
        addTrigger,
        deleteTrigger,
        getAvailableRuleAssessmentsSuccess,
        reset,
        restoreTrigger,
        setInitialData,
        toggleDeleteProtocolModal,
    } = actions;
    //#endregion

    //#region Click/Action/Event Handlers
    const goToLandingPage = () => navigate(protocolManagerRoute);

    const deleteTriggerHandler = R.curry((triggerId, policyAssessmentScheduleId, ruleData, event) => {
        event.preventDefault();
        deleteTrigger(triggerId, policyAssessmentScheduleId, ruleData);
    });

    const copyHandler = R.curry((protocolData, event) => {
        event.preventDefault();
        send(MACHINE_EVENTS.SAVE);
        ProtocolService.copyProtocol(protocolData)
            .then(goToLandingPage)
            .catch((error) => {
                toast.error(error.message);
            });
    });

    const deleteProtocolHandler = R.curry((protocolData, event) => {
        event.preventDefault();

        // create delete Confirm function
        toggleDeleteProtocolModal(protocolData);
    });

    const saveHandler = (values, formikBag) => {
        send(MACHINE_EVENTS.SAVE);

        Promise.resolve()

            .then(() => {
                if (hasLength(addedTriggers)) {
                    return Promise.all(
                        R.map(({ remoteTrigger: rule }) => {
                            return ProtocolService.createTrigger(protocolId, rule).then(({ id: triggerId }) => {
                                if (hasLength(R.path(['profileTrigger', 'profileFilters'], rule))) {
                                    return ProtocolService.createProfileFilters(
                                        protocolId,
                                        triggerId,
                                        rule.profileTrigger.profileFilters
                                    );
                                }

                                if (hasLength(R.path(['activityTrigger', 'activityFilters'], rule))) {
                                    return ProtocolService.createAssessmentFilters(
                                        protocolId,
                                        triggerId,
                                        rule.activityTrigger.activityFilters
                                    );
                                }
                            });
                        }, addedTriggers)
                    );
                }
            })

            .then(() => {
                if (hasLength(deletedTriggers)) {
                    return ProtocolService.deleteTriggers(
                        protocolId,
                        R.map(R.prop('policyAssessmentScheduleId'), deletedTriggers)
                    );
                }
            })
            .then(() => {
                const drugAny = R.includes(allMedicationsOption)(values.medications) ? 1 : 0;

                const diagnosisAny = R.ifElse(
                    hasLength,
                    R.ifElse(R.includes(allDiagnosisOption), R.always(1), R.always(0)),
                    R.always(0)
                )(values.diagnosisCodes);

                return ProtocolService.updateProtocol({
                    id: protocolId,
                    name: values.name,
                    drugAny,
                    medications: drugAny
                        ? []
                        : R.compose(R.map(R.prop('id')), R.without([allMedicationsOption]))(values.medications),
                    diagnosisAny,
                    diagnoses: diagnosisAny
                        ? []
                        : R.compose(
                              R.map(R.prop('code')),
                              R.without([allDiagnosisOption])
                          )(values.diagnosisCodes || []),
                    enabled: 1,
                });
            })
            .then((savedProtocol) =>
                ProtocolService.getHydratedProtocol(protocolId).then(R.assoc('protocol', savedProtocol))
            )
            .then(setInitialData)
            .then(() => {
                queryClient.invalidateQueries(['protocols', protocolId, 'triggers']);
                setTriggerPage(1);
                formikBag.setSubmitting(false);
                send(MACHINE_EVENTS.SAVE_COMPLETE);
                toast.success('Protocol saved successfully.');
            })
            .catch((error) => {
                formikBag.setSubmitting(false);
                send(MACHINE_EVENTS.SAVE_COMPLETE);
                errorHandler(error);
            });
    };

    const addRule = (rule) => {
        R.compose(addTrigger, R.assocPath(['remoteTrigger', 'therapyPolicyId'], protocolId))(rule);

        send(MACHINE_EVENTS.ADD_RULE_COMPLETE);
    };
    //#endregion

    //#region Side Effects
    useEffect(() => {
        if (protocolId) {
            ProtocolService.getHydratedProtocol(protocolId)
                .then(setInitialData)
                .then(() => send(MACHINE_EVENTS.EDIT))
                .catch((error) => {
                    toast.error(error.message);
                });
        } else {
            reset();
        }
    }, [protocolId, reset, send, setInitialData]);

    useEffect(() => {
        if (protocol && !isReadOnly) {
            const promises = [
                AssessmentService.getTherapyAssessments(protocol.therapyId, state.showRetiredAssessments),
                AssessmentService.getGeneralTherapyAssessments(),
                getReferralTypes(),
            ];
            Promise.all(promises).then(getAvailableRuleAssessmentsSuccess);
        }
    }, [protocol, isReadOnly, getAvailableRuleAssessmentsSuccess, state.showRetiredAssessments]);

    //#region Collapse Rules on New Protocols
    /**
     * This collection of effects and the reducer and ref are all just to get the "Rules" table to close after loading
     * has completed, and there are no rules, indicating a new protocol and then hand control back to the user.
     * This is because it may be difficult for users
     * to find the newly added "Changes" table that was added to address the payload size issues.
     */
    useEffect(() => {
        rulesAccordionInitRef.current = false;
        dispatchRulesAccordion('RESET');
    }, [protocolId]);

    useEffect(() => {
        if (rulesAccordionInitRef.current) return;
        if (triggersLoading) return;
        rulesAccordionInitRef.current = true;
        if (totalTriggers === 0) {
            dispatchRulesAccordion('CLOSE');
        }
    }, [triggersLoading, totalTriggers]);

    useEffect(() => {
        if (rulesAccordionControlled === false) {
            dispatchRulesAccordion('RELEASE');
        }
    }, [rulesAccordionControlled]);
    //#endregion

    //#endregion

    //#region Runtime Calculations
    /* eslint-disable react/no-unstable-nested-components, react/prop-types */
    const tableColumns = useMemo(() => {
        const columns = [
            {
                Header: 'Event',
                style: {
                    width: '20%',
                },
                SubHeader: 'The Action that will initiate this rule',
                accessor: 'event',
                id: 'event',
                canSort: true,
            },
            {
                Header: 'Activity Triggered',
                style: {
                    width: '20%',
                },
                SubHeader: 'The activity that will be triggered',
                accessor: 'activityTriggered',
                id: 'activityTriggered',
                canSort: true,
            },
            {
                style: {
                    width: '30%',
                },
                Header: 'Conditions',
                SubHeader: 'Criteria that triggers the new activity',
                accessor: 'conditions',
                id: 'conditions',
                canSort: true,
                Cell: ({ value, row }) => {
                    const filterKey = R.cond([
                        [R.propEq(TRIGGER_TYPES.REFERRAL_STATUS, 'type'), R.always('filter')],
                        [R.propEq(TRIGGER_TYPES.INSURANCE, 'type'), R.always('filter')],
                        [
                            R.anyPass(
                                R.compose(
                                    R.map(R.compose(R.propEq(R.__, 'referralActivityType'), Number)),
                                    R.keys
                                )(REFERRAL_TYPES)
                            ),
                            R.always('filter'),
                        ],
                        [R.propEq(TRIGGER_TYPES.PROFILE, 'type'), R.always('profileFilters')],
                        [R.T, R.always('activityFilters')],
                    ])(row.original);

                    const filters = R.path(['original', filterKey], row);

                    if (hasLength(filters)) {
                        return R.compose(
                            conditionRenderer,
                            R.map((filter) => {
                                const hidden = !!R.pathOr(
                                    false,
                                    ['questions', filter.questionId, 'attributes', 'hidden'],
                                    row.original
                                );
                                return R.assoc('hidden', hidden, filter);
                            })
                        )(filters);
                    }

                    if (value) {
                        return value;
                    }

                    return '--';
                },
            },
            {
                style: {
                    width: '20%',
                },
                Header: 'Due Date Configuration',
                SubHeader: 'Determines the new Activity Due Date',
                accessor: 'dueDate',
                id: 'dueDate',
                canSort: true,
                Cell: ({ row }) => {
                    const isHidden = R.pathOr(
                        false,
                        ['questions', row.original.dateFieldId, 'attributes', 'hidden'],
                        row.original
                    )
                        ? '(HIDDEN)'
                        : '';
                    return (
                        <>
                            <span>{`${row.original.dueDateValues.days} `}</span>
                            <strong>
                                {`${isHidden} `}
                                {row.original.dueDateValues.field}
                            </strong>
                        </>
                    );
                },
            },
        ];

        if (!isReadOnly) {
            return [
                ...columns,
                {
                    Header: 'Action',
                    id: 'action',
                    style: {
                        width: '10%',
                    },
                    Cell: ({ row }) => {
                        return (
                            <ActionCell style={{ minWidth: '5.625rem' }}>
                                <ActionButtonLink
                                    variant="link"
                                    onClick={deleteTriggerHandler(
                                        row.original.id,
                                        row.original.policyAssessmentScheduleId,
                                        row.original
                                    )}
                                >
                                    Delete
                                </ActionButtonLink>
                            </ActionCell>
                        );
                    },
                },
            ];
        }
        return columns;
    }, [deleteTriggerHandler, isReadOnly]);

    const changesData = useMemo(() => {
        const added = addedTriggers.map((t) => ({ ...t, _changeType: 'added' }));
        const deleted = deletedTriggers.map((t) => ({ ...t, _changeType: 'deleted' }));
        return [...added, ...deleted];
    }, [addedTriggers, deletedTriggers]);

    const changesColumns = useMemo(() => {
        const dataColumns = tableColumns.slice(0, 4);
        return [
            {
                Header: () => 'Status',
                id: 'changeStatus',
                style: { width: '10%' },
                Cell: ({ row }) => {
                    const isAdded = row.original._changeType === 'added';
                    return (
                        <ChangeIndicator style={{ color: isAdded ? '#28a745' : '#dc3545' }}>
                            {isAdded ? <FaPlus size={12} /> : <FaMinus size={12} />}
                            {isAdded ? 'Adding' : 'Removing'}
                        </ChangeIndicator>
                    );
                },
            },
            ...dataColumns,
            {
                Header: () => 'Action',
                id: 'changesAction',
                style: { width: '10%' },
                Cell: ({ row }) => {
                    const isAdded = row.original._changeType === 'added';
                    if (isAdded) {
                        return (
                            <ActionCell style={{ minWidth: '5.625rem' }}>
                                <ActionButtonLink
                                    variant="link"
                                    onClick={deleteTriggerHandler(
                                        row.original.id,
                                        row.original.policyAssessmentScheduleId,
                                        row.original
                                    )}
                                >
                                    Delete
                                </ActionButtonLink>
                            </ActionCell>
                        );
                    }
                    return (
                        <ActionCell style={{ minWidth: '5.625rem' }}>
                            <ActionButtonLink
                                variant="link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    restoreTrigger(row.original.policyAssessmentScheduleId);
                                }}
                            >
                                Restore
                            </ActionButtonLink>
                        </ActionCell>
                    );
                },
            },
        ];
    }, [tableColumns, deleteTriggerHandler, restoreTrigger]);

    const handleTriggerFilterChange = useCallback(
        (field) => (e) => {
            setTriggerFilters((prev) => ({ ...prev, [field]: e.target.value }));
        },
        []
    );

    const TriggerFilterInput = useCallback(
        ({ column }) => (
            <TextInput
                name={`filter-${column.id}`}
                placeholder="Filter…"
                onChange={handleTriggerFilterChange(column.id)}
            />
        ),
        [handleTriggerFilterChange]
    );

    const serverTableColumns = useMemo(() => {
        const [eventCol, activityCol, conditionsCol, dueDateCol, ...rest] = tableColumns;
        const withFilter = (col) => ({ ...col, canFilter: true, Filter: TriggerFilterInput });
        return [
            withFilter(eventCol),
            withFilter(activityCol),
            withFilter(conditionsCol),
            withFilter(dueDateCol),
            ...rest,
        ];
    }, [tableColumns, TriggerFilterInput]);
    /* eslint-enable react/no-unstable-nested-components, react/prop-types */

    const isSaveDisabled = (formik) => {
        if (formik.isSubmitting) return true;

        if (hasLength(deletedTriggers) || hasLength(addedTriggers)) {
            return false;
        }

        const isClean = !formik.dirty;
        const isInvalid = !formik.isValid;

        const arr = [
            isClean,
            R.anyPass([R.equals(MACHINE_STATES.SAVING), R.equals(MACHINE_STATES.LOADING)])(machineState.value),
            isReadOnly,
            isInvalid,
        ];

        return R.reduce((result, value) => result || value, false, arr);
    };
    //#endregion

    const isTableDataLoading =
        triggersLoading ||
        R.anyPass([R.equals(MACHINE_STATES.LOADING), R.equals(MACHINE_STATES.SAVING)])(machineState.value);

    return (
        <>
            <Helmet>
                <title>
                    {isReadOnly ? 'View' : 'Edit'} Protocol - {env.catchPhrase}
                </title>
            </Helmet>
            <PageTitle>{isReadOnly ? 'View' : 'Edit'} Protocol</PageTitle>

            <PageWrapper>
                <Formik
                    key={machineState.value === MACHINE_STATES.LOADING}
                    initialValues={{
                        name: R.prop('name', protocol),
                        medications: selectedMedications,
                        diagnosisCodes: selectedDiagnosisCodes,
                    }}
                    onSubmit={saveHandler}
                    validationSchema={Yup.object().shape({
                        name: Yup.string()
                            .max(75, 'Name must be less than 75 characters.')
                            .required('A name is required'),
                        medications: Yup.array().required('A medication is required'),
                    })}
                >
                    {(formik) => (
                        <form data-testid="edit-protocol-form" onSubmit={formik.handleSubmit}>
                            <AccordionWrapper title="Protocol Settings" style={{ paddingTop: '0px' }}>
                                <ProtocolBaseForm
                                    category={R.ifElse(R.isNil, R.always(null), (p) =>
                                        R.mergeWith(
                                            R.assoc,
                                            {
                                                id: R.prop('therapyId', p),
                                                name: R.path(['therapy', 'name'], p),
                                            },
                                            {}
                                        )
                                    )(protocol)}
                                    isFormLoading={R.anyPass([R.equals(MACHINE_STATES.LOADING)])(machineState.value)}
                                    isDisabled={
                                        formik.isSubmitting ||
                                        isReadOnly ||
                                        R.anyPass([
                                            R.equals(MACHINE_STATES.LOADING),
                                            R.equals(MACHINE_STATES.SAVING),
                                            R.equals(MACHINE_STATES.ADDING_RULE),
                                        ])(machineState.value)
                                    }
                                    medicationOptions={medicationOptions}
                                    diagnosisCodeOptions={diagnosisCodeOptions}
                                />
                            </AccordionWrapper>

                            {R.equals(MACHINE_STATES.ADDING_RULE, machineState.value) ||
                                (!isReadOnly && (
                                    <Row>
                                        <Col md={12}>
                                            <Button
                                                variant="link"
                                                className="float-right"
                                                type="button"
                                                disabled={
                                                    !R.allPass([
                                                        R.pathOr(false, ['therapyId']),
                                                        R.pathOr(false, ['therapy', 'name']),
                                                    ])(protocol) ||
                                                    R.anyPass([
                                                        R.equals(MACHINE_STATES.LOADING),
                                                        R.equals(MACHINE_STATES.SAVING),
                                                    ])(machineState.value) ||
                                                    formik.isSubmitting
                                                }
                                                onClick={() => send(MACHINE_EVENTS.ADD_RULE)}
                                            >
                                                <FaPlus /> Add Rule
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}

                            {!R.equals(MACHINE_STATES.ADDING_RULE, machineState.value) && (
                                <>
                                    <AccordionWrapper
                                        title="Rules"
                                        stateKey="protocolSettings-Rules"
                                        controlledVisible={rulesAccordionControlled}
                                    >
                                        <Row>
                                            <Col md={12} data-testid="rules-table">
                                                <ManualPageTable
                                                    columns={serverTableColumns}
                                                    data={serverTriggers}
                                                    totalRows={totalTriggers}
                                                    currentPage={triggerPage}
                                                    currentPageIndex={triggerPageSize}
                                                    pageSizeOptions={[10, 25, 50]}
                                                    onPageChange={({ pageIndex, pageSize }) => {
                                                        setTriggerPage(pageIndex);
                                                        setTriggerPageSize(pageSize);
                                                    }}
                                                    onSortChange={({ sortKey, sortOrder }) => {
                                                        setTriggerSort({ key: sortKey, order: sortOrder });
                                                        setTriggerPage(1);
                                                    }}
                                                    isLoading={isTableDataLoading}
                                                    canSort
                                                    initialSortKey="event"
                                                    initialSortOrder="asc"
                                                    placeholderText="All rules for this protocol will appear here."
                                                />
                                            </Col>
                                        </Row>
                                    </AccordionWrapper>

                                    {!isReadOnly && hasLength(changesData) && (
                                        <AccordionWrapper title="Changes" stateKey="protocolSettings-Changes">
                                            <Row>
                                                <Col md={12}>
                                                    <Table
                                                        columns={changesColumns}
                                                        data={changesData}
                                                        placeholderText=""
                                                        isLoading={isTableDataLoading}
                                                    />
                                                </Col>
                                            </Row>
                                        </AccordionWrapper>
                                    )}
                                </>
                            )}

                            {R.equals(MACHINE_STATES.ADDING_RULE, machineState.value) ? null : (
                                <Row>
                                    <Col md={12}>
                                        <div className="form-button-group">
                                            {shouldShowProtocolDelete({ hasDeleteTherigyProtocol, isReadOnly }) && (
                                                <Button
                                                    variant="danger"
                                                    type="button"
                                                    aria-label="Delete Protocol"
                                                    disabled={isProtocolDeleteDisabled({
                                                        machineValue: machineState.value,
                                                        isSubmitting: formik.isSubmitting,
                                                        isReadOnly,
                                                        hasDeleteTherigyProtocol,
                                                    })}
                                                    onClick={deleteProtocolHandler(protocol)}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline-secondary"
                                                type="button"
                                                aria-label="Copy Protocol"
                                                disabled={R.anyPass([
                                                    R.equals(MACHINE_STATES.LOADING),
                                                    R.equals(MACHINE_STATES.SAVING),
                                                    R.always(formik.isSubmitting),
                                                ])(machineState.value)}
                                                onClick={copyHandler(protocol)}
                                            >
                                                Copy
                                            </Button>

                                            <Button
                                                variant="outline-secondary"
                                                onClick={goToLandingPage}
                                                aria-label="Back to protocol page"
                                                type="button"
                                                disabled={
                                                    R.anyPass([
                                                        R.equals(MACHINE_STATES.LOADING),
                                                        R.equals(MACHINE_STATES.SAVING),
                                                    ])(machineState.value) || formik.isSubmitting
                                                }
                                            >
                                                {isReadOnly ? 'Go Back' : 'Cancel'}
                                            </Button>

                                            {!isReadOnly && (
                                                <Button
                                                    variant="primary"
                                                    type="submit"
                                                    aria-label="Save"
                                                    disabled={isSaveDisabled(formik)}
                                                >
                                                    Save
                                                </Button>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </form>
                    )}
                </Formik>

                {R.equals(MACHINE_STATES.ADDING_RULE, machineState.value) && (
                    <AddRule onCancel={() => send(MACHINE_EVENTS.CANCEL)} onAddRule={addRule} />
                )}
            </PageWrapper>

            {showDeleteProtocolModal && (
                <DeleteModal
                    onHide={() => toggleDeleteProtocolModal()}
                    show
                    onDelete={() => {
                        ProtocolService.deleteProtocol(protocolToDelete)
                            .then(goToLandingPage)
                            .catch((error) => {
                                toast.error(error.message);
                                toggleDeleteProtocolModal();
                            });
                    }}
                    title="Delete Protocol"
                    // DeleteModal expects a render callback for custom message content.
                    // eslint-disable-next-line react/no-unstable-nested-components
                    message={({ style }) => {
                        return (
                            <>
                                {!R.path(['companyId'], protocolToDelete) && (
                                    <Alert variant="warning">
                                        Warning! You are about to delete a Therigy-owned protocol.
                                    </Alert>
                                )}
                                <p style={style}>Are you sure you want to delete this protocol?</p>
                            </>
                        );
                    }}
                />
            )}
        </>
    );
};

EditProtocolPage.propTypes = propTypes;
EditProtocolPage.defaultProps = defaultProps;

export default EditProtocolPage;
